import axios from 'axios'

import { toCamelCase } from '../helpers/utils'
import { CoreDocument } from './document'

export enum PlaidAccountType {
  DEPOSITORY = 'DEPOSITORY',
}

export enum PlaidAccountSubtype {
  SAVINGS = 'savings',
  CHECKING = 'checking',
}

export enum PlaidProduct {
  AUTH = 'auth',
  ASSETS = 'assets',
  EXTENSION_OF_CREDIT = 'extension_of_credit',
  IDENTITY_VERIFICATION = 'identity_verification',
  CRA_BASE_REPORT = 'cra_base_report',
  CRA_INCOME_INSIGHTS = 'cra_income_insights',
  CRA_CASHFLOW_INSIGHTS = 'cra_cashflow_insights',
  CRA_LEND_SCORE = 'cra_lend_score',
  CRA_PARTNER_INSIGHTS = 'cra_partner_insights',
  CRA_NETWORK_INSIGHTS = 'cra_network_insights',
}

export interface PlaidItem {
  itemId?: string
  institutionName: string
  hasAssetsProduct: boolean
}

export enum PlaidAccountSource {
  PAYMENT_PROFILE = 'PAYMENT_PROFILE',
  CONTRACTOR_PAYMENTS = 'CONTRACTOR_PAYMENTS',
}

export interface PlaidAccount {
  accountId: string
  accountType: string
  accountSubtype: string
  mask: string
  name: string
  verificationStatus: PlaidAccountStatus
  item: PlaidItem
  isExpired: boolean
  isActive: boolean
  source: PlaidAccountSource
}

export interface PlaidAccountResponse {
  account_id: string
  account_type: PlaidAccountType
  account_subtype: PlaidAccountSubtype
  mask: string
  name: string
  verification_status: PlaidAccountStatus | ''
  is_active: boolean
  source: PlaidAccountSource
  item: {
    item_id?: string
    institution_name: string
    has_assets_product: boolean
  }
}

export interface PlaidUser {
  id: string
  dateCreated: string
  dateUpdated: string
  dateVerified: string
  clientUserId: string
  products: PlaidProduct[]
  reportPurpose: string
  plaidConsumerReports: PlaidConsumerReport[]
  isComplete: boolean
}

export interface PlaidConsumerReport {
  id: string
  dateCreated: string
  dateUpdated: string
  status: string
  reportId: string
  type: PlaidConsumerReportType
  reportDocument?: CoreDocument<{}>
}

export enum PlaidConsumerReportType {
  CRA_BASE = 'CRA_BASE',
  CRA_BASE_REPORT = 'CRA_BASE_REPORT',
  CRA_INCOME_INSIGHTS = 'CRA_INCOME_INSIGHTS',
  CRA_PARTNER_INSIGHTS = 'CRA_PARTNER_INSIGHTS',
  CRA_MONITORING_INSIGHTS = 'CRA_MONITORING_INSIGHTS',
  CRA_LEND_SCORE = 'CRA_LEND_SCORE',
  CRA_VERIFICATION = 'CRA_VERIFICATION',
}

export const PlaidConsumerReportTypeLabel = {
  [PlaidConsumerReportType.CRA_BASE]: 'CRA Base Report',
  [PlaidConsumerReportType.CRA_BASE_REPORT]: 'CRA Base Report',
  [PlaidConsumerReportType.CRA_INCOME_INSIGHTS]: 'CRA Income Insights',
  [PlaidConsumerReportType.CRA_PARTNER_INSIGHTS]: 'CRA Partner Insights',
  [PlaidConsumerReportType.CRA_MONITORING_INSIGHTS]: 'CRA Monitoring Insights',
  [PlaidConsumerReportType.CRA_LEND_SCORE]: 'CRA Lend Score',
  [PlaidConsumerReportType.CRA_VERIFICATION]: 'CRA Verification',
}

export interface PlaidAccountError {
  detail: string
}

export enum PlaidAccountStatus {
  PENDING_AUTOMATIC_VERIFICATION = 'pending_automatic_verification',
  PENDING_MANUAL_VERIFICATION = 'pending_manual_verification',
  AUTOMATICALLY_VERIFIED = 'automatically_verified',
  MANUALLY_VERIFIED = 'manually_verified',
  VERIFICATION_EXPIRED = 'verification_expired',
  VERIFICATION_FAILED = 'verification_failed',
}

export interface PlainLinkToken {
  linkToken: string
}

// Helpers

export function isPlaidAccountVerified(account?: PlaidAccount) {
  return (
    account &&
    (account.verificationStatus === PlaidAccountStatus.AUTOMATICALLY_VERIFIED ||
      account.verificationStatus === PlaidAccountStatus.MANUALLY_VERIFIED)
  )
}

export function isPlaidAccountPending(account?: PlaidAccount) {
  return (
    account &&
    (account.verificationStatus === PlaidAccountStatus.PENDING_AUTOMATIC_VERIFICATION ||
      account.verificationStatus === PlaidAccountStatus.PENDING_MANUAL_VERIFICATION)
  )
}

export function parsePlaidAccount(account: PlaidAccountResponse): PlaidAccount {
  return {
    accountId: account.account_id,
    accountType: account.account_type,
    accountSubtype: account.account_subtype,
    mask: account.mask,
    name: account.name,
    // if verification status isn't explicitly provided it's assumed verified
    verificationStatus: account.verification_status || PlaidAccountStatus.AUTOMATICALLY_VERIFIED,
    isExpired: account.verification_status === PlaidAccountStatus.VERIFICATION_EXPIRED,
    item: {
      itemId: account.item?.item_id || '',
      institutionName: account.item?.institution_name || '',
      hasAssetsProduct: account.item?.has_assets_product,
    },
    isActive: account.is_active,
    source: account.source,
  }
}

export function getPlaidDetails(userId?: string): Promise<{ plaidUser: PlaidUser | null }> {
  return axios.get(`/api/v3/plaid-details/?${userId ? `user=${userId}` : ''}`).then((response) => {
    return { plaidUser: toCamelCase(response.data.plaid_user) as PlaidUser }
  })
}
